import api from "./apiBase";
import store from "@/store";

function currentRevision() {
  const r = Number(store?.state?.cart?.summary?.revision);
  return Number.isFinite(r) ? r : undefined;
}
function withIfMatch(headers = {}, override) {
  const rev = override != null ? Number(override) : currentRevision();
  if (Number.isFinite(rev) && rev > 0) headers["If-Match"] = String(rev);
  return headers;
}

/** Normaliza a resposta do backend para um shape amigável ao front */
function normalizeCart(payload) {
  const src = payload?.data?.cart ?? payload; // aceita {cart: {...}} ou direto
  const items = Array.isArray(src?.items) ? src.items : [];
  const normItems = items.map((it) => ({ ...it }));
  return {
    id: src?.id ?? null,
    userId: src?.userId ?? null,
    status: src?.status ?? null,
    revision: Number(src?.revision ?? 0),
    items: normItems,
  };
}

/** GET /cart (leitura pura). Se includeAbandoned=true, devolve abandonado se não houver ACTIVE. */
export async function fetchCart({
  includeAbandoned = true,
  ifNoneMatch,
  signal,
} = {}) {
  const headers = {};
  if (typeof ifNoneMatch === "number" && ifNoneMatch > 0) {
    headers["If-None-Match"] = String(ifNoneMatch);
  }
  const res = await api.get("/cart", {
    params: { includeAbandoned },
    headers,
    signal,
    validateStatus: (s) => s === 200 || s === 304 || s === 401,
  });

  if (res.status === 304) return null; // nada mudou
  if (res.status === 401) throw new Error("Unauthorized");

  return normalizeCart(res.data);
}

/** POST /cart — garante ACTIVE (reativa abandonado ou cria) e retorna o carrinho */
export async function ensureActiveCart({ metadata, signal } = {}) {
  const res = await api.post("/cart", metadata ? { metadata } : {}, { signal });
  return normalizeCart(res.data);
}

/** PATCH /cart/items/:id — atualiza parcial (quantity/config). Requer If-Match */
export async function patchCartItem(itemId, body, { ifMatch, signal } = {}) {
  const headers = withIfMatch({}, ifMatch); // ← usa revision do store por padrão

  const res = await api.patch(
    `/cart/items/${encodeURIComponent(itemId)}`,
    body,
    {
      headers,
      signal,
      validateStatus: (s) => (s >= 200 && s < 300) || s === 401 || s === 409,
    }
  );

  if (res.status === 409) {
    const err = new Error("Precondition Failed");
    err.code = 409;
    throw err;
  }
  if (res.status === 401) {
    const err = new Error("Unauthorized");
    err.code = 401;
    throw err;
  }

  // Identifica resposta "leve" (summary) vinda do backend
  const d = res.data?.data ?? res.data ?? {};
  const looksLikeSummary =
    d &&
    (Number.isFinite(Number(d.itemCount)) ||
      Number.isFinite(Number(d.quantityTotal)) ||
      Number.isFinite(Number(d.revision))) &&
    !d.cart;

  if (looksLikeSummary) {
    return {
      itemCount: Number(d.itemCount ?? 0),
      quantityTotal: Number(d.quantityTotal ?? 0),
      revision: Number(d.revision ?? 0),
    };
  }

  return normalizeCart(res.data);
}

/** POST /cart/items — adiciona item. Requer If-Match */
export async function addCartItem(
  payload,
  { ifMatch, idemKey, signal, silentOnUnauthorized = true } = {}
) {
  const headers = withIfMatch({}, ifMatch);
  if (idemKey) headers["X-Idempotency-Key"] = idemKey;

  const res = await api.post("/cart/items", payload, {
    headers,
    signal,
    validateStatus: (s) =>
      (s >= 200 && s < 300) || [400, 401, 404, 409, 422].includes(s),
  });

  const body = res?.data ?? {};

  // ---------- CASO 1: Result.success ----------
  if (body?.status === "success") {
    const d = body?.data ?? {};
    return {
      ok: true,
      itemCount: Number(d?.itemCount ?? 0),
      quantityTotal: Number(d?.quantityTotal ?? 0),
      revision: Number(d?.revision ?? 0),
      status: res.status,
      message: body?.message,
    };
  }

  // 401 (não logado)
  if (res.status === 401) {
    if (silentOnUnauthorized) {
      return { ok: false, status: 401, code: "UNAUTHORIZED_SILENT" };
    }
    const err = new Error("Unauthorized");
    err.response = res;
    throw err;
  }

  // ---------- CASO 2: Result.error (formato novo) ----------
  if (body?.status === "error") {
    const dataNode = body?.data ?? {};
    const code =
      dataNode?.code ??
      body?.code ??
      (res.status === 404
        ? "DESIGN_NOT_FOUND"
        : res.status === 422
        ? "DESIGN_RESTRICTED_NO_MAKERS"
        : res.status === 409
        ? // se o back não mandou code, assuma conflito genérico
          "CONFLICT"
        : "BAD_REQUEST");

    const message =
      body?.message ??
      (code === "CART_RESTRICTED_ALREADY_PRESENT"
        ? "Seu carrinho já possui itens vinculados a makers específicos. Esvazie o carrinho para adicionar este item."
        : code === "DESIGN_RESTRICTED_NO_MAKERS"
        ? "Este produto está indisponível para impressão no momento."
        : code === "DESIGN_NOT_FOUND"
        ? "Produto não encontrado."
        : code === "PRECONDITION_FAILED"
        ? "Carrinho desatualizado. Recarregue e tente novamente."
        : "Erro na solicitação.");

    const details =
      (Object.prototype.hasOwnProperty.call(dataNode, "details")
        ? dataNode.details
        : undefined) ??
      (Object.prototype.hasOwnProperty.call(body, "details")
        ? body.details
        : null);

    return {
      ok: false,
      status: res.status,
      code,
      message,
      details: details ?? null,
      raw: body,
    };
  }

  // ---------- CASO 3: Formatos legados (sem status) ----------
  // Tente inferir PRECONDITION_FAILED a partir da mensagem 409
  const msg = String(body?.message || body?.error || "").toLowerCase();
  const isRevConflict =
    res.status === 409 &&
    (msg.includes("revision conflict") ||
      msg.includes("if-match") ||
      msg.includes("precondition"));

  const fallbackCodeByStatus = {
    400: "BAD_REQUEST",
    404: "DESIGN_NOT_FOUND",
    409: isRevConflict
      ? "PRECONDITION_FAILED"
      : "CART_RESTRICTED_ALREADY_PRESENT",
    422: "DESIGN_RESTRICTED_NO_MAKERS",
  };

  const legacyCode =
    body?.code ?? fallbackCodeByStatus[res.status] ?? "BAD_REQUEST";

  const legacyMsg =
    body?.message ??
    (legacyCode === "CART_RESTRICTED_ALREADY_PRESENT"
      ? "Seu carrinho já possui itens vinculados a makers específicos. Esvazie o carrinho para adicionar este item."
      : legacyCode === "DESIGN_RESTRICTED_NO_MAKERS"
      ? "Este produto está indisponível para impressão no momento."
      : legacyCode === "DESIGN_NOT_FOUND"
      ? "Produto não encontrado."
      : legacyCode === "PRECONDITION_FAILED"
      ? "Carrinho desatualizado. Recarregue e tente novamente."
      : "Erro na solicitação.");

  return {
    ok: false,
    status: res.status,
    code: legacyCode,
    message: legacyMsg,
    details: body?.details ?? null,
    raw: body,
  };
}

/** DELETE /cart/items/:id — remove item. Requer If-Match */
export async function removeCartItem(itemId, { ifMatch, signal } = {}) {
  const headers = withIfMatch({}, ifMatch); // ← usa revision do store

  const res = await api.delete(`/cart/items/${encodeURIComponent(itemId)}`, {
    headers,
    signal,
    validateStatus: (s) => s === 200 || s === 404 || s === 409,
  });

  if (res.status === 409) {
    const err = new Error("Precondition Failed");
    err.code = 409;
    throw err;
  }

  // resposta leve do backend
  const payload = res.data?.data ?? res.data ?? {};
  return {
    ok: payload.ok ?? res.status === 200,
    revision: Number(payload.revision ?? 0),
    itemCount: Number(payload.itemCount ?? 0),
    quantityTotal: Number(payload.quantityTotal ?? 0),
  };
}

/**
 * GET /cart/summary — Busca o summary do carrinho.
 * - Se enviar If-None-Match e nada mudou, retorna `null`.
 * - Se 200, retorna { itemCount, quantityTotal, revision }.
 * - Se 401 e silentOnUnauthorized=true, retorna zeros.
 */
export async function fetchCartSummary({
  ifNoneMatch,
  signal,
  silentOnUnauthorized = true,
} = {}) {
  const headers = {};
  if (typeof ifNoneMatch === "number" && ifNoneMatch > 0) {
    headers["If-None-Match"] = String(ifNoneMatch);
  }

  const res = await api.get("/cart/summary", {
    params: { includeAbandoned: true },
    headers,
    signal,
    validateStatus: (s) => s === 200 || s === 304 || s === 401,
  });

  if (res.status === 304) return null;

  if (res.status === 401) {
    if (silentOnUnauthorized) {
      return { itemCount: 0, quantityTotal: 0, revision: 0 };
    }
    throw new Error("Unauthorized");
  }

  return {
    itemCount: Number(res.data?.data.itemCount ?? 0),
    quantityTotal: Number(res.data?.data.quantityTotal ?? 0),
    revision: Number(res.data?.data.revision ?? 0),
  };
}

/** DELETE /cart/items — limpa o carrinho inteiro. Requer If-Match */
export async function clearCartItems({
  ifMatch,
  signal,
  silentOnUnauthorized = true,
} = {}) {
  const headers = withIfMatch({}, ifMatch); // ← usa revision do store

  const res = await api.delete("/cart/items", {
    headers,
    signal,
    validateStatus: (s) => s === 200 || s === 401 || s === 409,
  });

  if (res.status === 409) {
    const err = new Error("Precondition Failed");
    err.code = 409;
    throw err;
  }

  if (res.status === 401) {
    if (silentOnUnauthorized) {
      return { ok: false, revision: 0, itemCount: 0, quantityTotal: 0 };
    }
    const err = new Error("Unauthorized");
    err.code = 401;
    throw err;
  }

  // backend responde CartSummaryResponseDto (geralmente em data)
  const payload = res.data?.data ?? res.data ?? {};
  return {
    ok: payload.ok ?? res.status === 200,
    revision: Number(payload.revision ?? 0),
    itemCount: Number(payload.itemCount ?? 0),
    quantityTotal: Number(payload.quantityTotal ?? 0),
  };
}

/** POST /cart/items/swap — remove um design e adiciona outro. Requer If-Match */
export async function swapCartDesign(
  { removeDesignId, addDesignId },
  { ifMatch, idemKey, signal, silentOnUnauthorized = true } = {}
) {
  const headers = withIfMatch({}, ifMatch);
  if (idemKey) headers["X-Idempotency-Key"] = idemKey;

  const res = await api.post(
    "/cart/items/swap",
    { removeDesignId, addDesignId },
    {
      headers,
      signal,
      // Aceita também 4xx padronizados
      validateStatus: (s) =>
        (s >= 200 && s < 300) ||
        s === 400 ||
        s === 401 ||
        s === 404 ||
        s === 409 ||
        s === 422,
    }
  );

  const body = res?.data ?? {};

  // ------------------ Sucesso ------------------
  if (body?.status === "success") {
    const d = body?.data ?? {};
    return {
      ok: true,
      status: res.status,
      message: body?.message,
      itemCount: Number(d.itemCount ?? 0),
      quantityTotal: Number(d.quantityTotal ?? 0),
      revision: Number(d.revision ?? 0),
    };
  }

  // ------------------ 401 ------------------
  if (res.status === 401) {
    if (silentOnUnauthorized) {
      return { ok: false, status: 401, code: "UNAUTHORIZED_SILENT" };
    }
    const err = new Error("Unauthorized");
    err.response = res;
    throw err;
  }

  // ------------------ Erro padronizado ------------------
  if (body?.status === "error") {
    return {
      ok: false,
      status: res.status,
      code: body?.data?.code || "ERROR",
      message: body?.message || "Falha ao atualizar o carrinho.",
      details: body?.data?.details ?? null,
    };
  }

  // ------------------ Fallback ------------------
  return {
    ok: false,
    status: res.status,
    code: body?.code || "ERROR",
    message: body?.message || "Falha ao atualizar o carrinho.",
    details: body?.details ?? null,
  };
}
